import { useMemo } from "react";
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import SearchInputFieldWithDebounce from "components/PimDashboard/CommonComponents/SearchInputFieldWithDebounce/SearchInputFieldWithDebounce";
import { datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";
import { poMasterCacheKeyByColumn } from "../shared/bqMasterDropdownCache";
import { poColors } from "./poUi";
import { fetchPoVendorsForSite, nextPoDate, poMasterFetches, poPartyCode } from "./data";

export const fieldWidth = (column) =>
	column?.data_type === datatypes.LONGTEXT.key || column?.key === "remarks" ? "100%" : "32%";

const PoEntryModeRadios = ({ column, data, setData, disabled = false, hideLabel = false }) => {
	const options = column?.[selectOptionsKeyName]?.list || [];
	const value = data?.[column.key] || "I";
	const required = !!column?.constraints?.NOTNULL;

	return (
		<Box sx={{ width: "100%" }}>
			{!hideLabel && (
				<Typography sx={{ fontSize: 13, fontWeight: 600, color: poColors.muted, mb: 0.5 }}>
					{column.name}
					{required ? (
						<Box component="span" sx={{ color: poColors.danger, ml: 0.25 }}>
							*
						</Box>
					) : null}
				</Typography>
			)}
			<RadioGroup
				row
				name={column.key}
				value={value}
				onChange={(e) => {
					if (disabled) return;
					const next = e.target.value;
					setData((prev) => ({ ...prev, [column.key]: next }));
				}}
				sx={{
					gap: 1.5,
					flexWrap: "nowrap",
					minHeight: 40,
					alignItems: "center",
					"& .MuiFormControlLabel-root": { mr: 0 },
					"& .MuiRadio-root": { p: 0.4 },
					"& .MuiFormControlLabel-label": { fontSize: 14, color: poColors.ink },
				}}>
				{options.map((opt) => (
					<FormControlLabel
						key={String(opt.key)}
						value={opt.key}
						disabled={disabled}
						control={
							<Radio
								size="small"
								sx={{
									color: poColors.line,
									"&.Mui-checked": { color: poColors.ink },
									"&.Mui-disabled": { color: "rgba(0,0,0,0.28)" },
									"&.Mui-disabled.Mui-checked": { color: "rgba(0,0,0,0.45)" },
								}}
							/>
						}
						label={opt.name}
					/>
				))}
			</RadioGroup>
		</Box>
	);
};

const PoColumnField = ({
	column,
	data,
	setData,
	readOnly = false,
	error,
	width,
	dense = false,
	hideLabel = false,
	floatingLabel = false,
	placeholder,
	selectedLabel,
}) => {
	const isLocked = readOnly || !!column.disabled;
	const disabledColumn =
		isLocked && column.key !== "is_consignment"
			? { ...column, input_type: inputTypes.AUTO_GENERATE.key, editable: false }
			: column;
	const wrapSx = { width: width || fieldWidth(column), mb: dense ? 0 : 1.5 };
	const suppressExternalLabel = hideLabel || floatingLabel;
	// Keep search column shape when locked; memoize so mount fetch effect does not re-fire every render.
	const searchColumn = useMemo(
		() => (isLocked ? { ...column, editable: false } : column),
		[column, isLocked]
	);
	const ownerSiteCode = poPartyCode(data?.owner_site_code);
	const fetchData = useMemo(() => {
		if (column.key === "vendor_code") return fetchPoVendorsForSite(ownerSiteCode);
		return poMasterFetches[column.key];
	}, [column.key, ownerSiteCode]);

	if (column.key === "entry_mode") {
		return (
			<Box sx={wrapSx}>
				<PoEntryModeRadios
					column={column}
					data={data}
					setData={setData}
					disabled={isLocked}
					hideLabel={suppressExternalLabel}
				/>
			</Box>
		);
	}

	if (column.use_bq_search) {
		const isBarcode = column.key === "barcode";
		return (
			<Box sx={wrapSx}>
				<SearchInputFieldWithDebounce
					column={searchColumn}
					data={data}
					setData={setData}
					fetchData={fetchData || (isBarcode ? poMasterFetches.barcode : undefined)}
					selectOptKeyName={selectOptionsKeyName}
					storeValueAs={column.key === "vendor_code" || isBarcode ? "object" : "key"}
					disabled={isLocked}
					width="100%"
					error={error}
					cacheKey={
						column.key === "vendor_code"
							? `${poMasterCacheKeyByColumn.vendor_code}_${ownerSiteCode ?? "none"}`
							: poMasterCacheKeyByColumn[column.key] || `bq_master_${column.key}`
					}
					disableClientFilter={isBarcode}
					disableLabel={suppressExternalLabel}
					floatingLabel={floatingLabel}
					labelSx={{ p: 1 }}
					placeholder={floatingLabel ? undefined : placeholder}
				/>
			</Box>
		);
	}

	return (
		<Box sx={wrapSx}>
			<RenderInputField
				column={disabledColumn}
				data={data}
				setData={setData}
				selectOptObjName={selectOptionsKeyName}
				width="100%"
				error={error}
				hideLabel={suppressExternalLabel}
				floatingLabel={floatingLabel}
				placeholder={floatingLabel ? undefined : placeholder}
				inputProps={
					column.key === "valid_from_date" && data?.order_date
						? { min: nextPoDate(data.order_date) }
						: column.key === "valid_to_date" && data?.valid_from_date
							? { min: nextPoDate(data.valid_from_date) }
							: undefined
				}
			/>
		</Box>
	);
};

export default PoColumnField;
